import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { getUser } from "@/lib/auth";
import { roleDisplayLabel } from "@/lib/auth";
import { AppHeader } from "@/components/AppHeader";
import { SideNav } from "@/components/SideNav";
import { KpiCard } from "@/components/KpiCard";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  GlassWater, IndianRupee, Store as StoreIcon, Target, AlertTriangle,
  Wallet, Smartphone, Users, TrendingUp, Package, Bell, CheckCircle2, Zap,
} from "lucide-react";
import { formatINR, formatNum, formatLakhs } from "@/lib/format";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, PieChart, Pie, Cell, Legend,
} from "recharts";

const RED = "#E11D2A"; const DARK = "#0A0A0A"; const GRAY = "#9CA3AF";
const GREEN = "#16A34A"; const AMBER = "#D97706";

export default function AdminDashboard({ readOnly = false, roleLabel }) {
  const user = getUser();
  const nav = useNavigate();
  const label = roleLabel || roleDisplayLabel(user?.role);
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [monthly, setMonthly] = useState(null);   // /client/monthly-report — for the "Monthly Target" KPI
  const [alerts, setAlerts] = useState([]);
  const [digest, setDigest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Monthly Target KPI must always cover the FULL calendar month (1st → last day)
      // regardless of today's date — never bounded by "today" or by any filter.
      const now = new Date();
      const monthFirst = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`;
      const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      const monthLast = `${nextMonth.getFullYear()}-${String(nextMonth.getMonth() + 1).padStart(2, "0")}-${String(nextMonth.getDate()).padStart(2, "0")}`;
      const [s, r, mr] = await Promise.all([
        api.get("/dashboard/summary"),
        api.get("/dashboard/stores"),
        api.get("/client/monthly-report", { params: { from_date: monthFirst, to_date: monthLast } }).catch(() => ({ data: null })),
      ]);
      setSummary(s.data); setRows(r.data); setMonthly(mr.data);
      if (!readOnly) {
        try { const a = await api.get("/alerts"); setAlerts(a.data); } catch {}
      }
      try { const d = await api.get("/digest/today"); setDigest(d.data); } catch {}
    } catch { toast.error("Failed to load dashboard"); }
    finally { setLoading(false); }
  }, [readOnly]);
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);

  const filtered = useMemo(() => {
    const q = filter.toLowerCase();
    return rows.filter((r) =>
      !q || r.store_name.toLowerCase().includes(q) || r.city.toLowerCase().includes(q) || r.store_code.toLowerCase().includes(q)
    );
  }, [rows, filter]);

  if (loading || !summary) return <div className="p-8 text-center text-gray-500">Loading control tower…</div>;

  const { kpis, trend, top_stores, city_wise, attendance, stock_health, payment_breakdown_7d } = summary;
  const attData = [
    { name: "Present", value: attendance.present, fill: GREEN },
    { name: "Late", value: attendance.late, fill: AMBER },
    { name: "Absent/Pending", value: attendance.absent, fill: GRAY },
  ];
  // ITEM 8 (2026-02-29 re-audit) — Card added; % breakdown over 7-day window from backend.
  const paymentData = !readOnly && payment_breakdown_7d && payment_breakdown_7d.total > 0 ? [
    { name: `Cash ${payment_breakdown_7d.cash_pct}%`, value: payment_breakdown_7d.cash, fill: DARK },
    { name: `UPI ${payment_breakdown_7d.upi_pct}%`, value: payment_breakdown_7d.upi, fill: RED },
    { name: `Card ${payment_breakdown_7d.card_pct}%`, value: payment_breakdown_7d.card, fill: AMBER },
  ].filter((p) => p.value > 0) : [];
  // ITEM 6 (2026-02-29 re-audit) — Top KPI card target basis = 400 × ACTIVE stores (was total_stores).
  const dailyTargetActive = 400 * Math.max(1, kpis.active_stores || 0);
  // ITEM 7 (2026-02-29 re-audit) — Cups row for the Days of Cover widget, sourced from rows[].cups_remaining.
  const cupsCoverBuckets = (() => {
    let low = 0, ok = 0, healthy = 0, noData = 0;
    for (const r of rows) {
      const c = r.cups_remaining;
      if (c === null || c === undefined) { noData++; continue; }
      if (c < 100) low++;
      else if (c < 500) ok++;
      else healthy++;
    }
    return { low, ok, healthy, noData };
  })();

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <AppHeader title="Control Tower" subtitle={`${label} · Live ${rows.length || 30} stores`} />
      <div className="flex-1 flex">
        <SideNav />
        <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 py-6 space-y-6">

          {/* Alerts strip */}
          {!readOnly && alerts.length > 0 && (
            <Card className="rounded-xl border-red-200 bg-red-50/60" data-testid="alerts-card">
              <div className="p-4 flex items-center gap-3">
                <div className="h-9 w-9 rounded-lg bg-brand-red text-white flex items-center justify-center">
                  <Bell className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-bold uppercase tracking-[0.1em] text-red-800">Active Alerts</div>
                  <div className="font-display text-lg font-bold text-gray-900">{alerts.length} needing attention</div>
                </div>
                <div className="flex flex-wrap gap-1.5 max-w-md">
                  {["low_stock","bib_low","no_attendance","low_target","payment_mismatch","cash_gap"].map((t) => {
                    const c = alerts.filter(a => a.type === t).length;
                    if (!c) return null;
                    return <Badge key={t} className="bg-white text-red-800 hover:bg-white border border-red-200">{c} {t.replace(/_/g," ")}</Badge>;
                  })}
                </div>
              </div>
              <div className="max-h-32 overflow-y-auto border-t border-red-100 divide-y divide-red-50">
                {alerts.slice(0, 8).map((a) => (
                  <div key={a.id} className="px-4 py-2 flex items-start gap-2 text-sm" data-testid={`alert-${a.type}`}>
                    <AlertTriangle className={`h-4 w-4 mt-0.5 shrink-0 ${a.severity === "high" ? "text-red-600" : "text-amber-600"}`} />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 truncate">{a.message}</div>
                      {a.meta?.reorder && (
                        <div className="text-[11px] text-red-800 font-mono truncate">{a.meta.reorder}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* KPI grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 stagger-in">
            <KpiCard testId="kpi-cups" label="Cups Today" value={formatNum(kpis.total_glasses)} icon={GlassWater} accent="bg-red-50 text-brand-red" sub={`Target ${formatNum(dailyTargetActive)}`} />
            <KpiCard testId="kpi-sales" label="Sales Today" value={formatLakhs(kpis.total_sales)} icon={IndianRupee} accent="bg-red-50 text-brand-red" sub="₹9 per cup" />
            <KpiCard testId="kpi-target" label="Target %" value={`${kpis.target_pct}%`} icon={Target} accent={kpis.target_pct>=80?"bg-green-50 text-green-700":"bg-amber-50 text-amber-700"} sub={kpis.target_pct>=80?"On track":"Below plan"} />
            <KpiCard testId="kpi-active" label="Stores Reported Today" value={`${kpis.active_stores}/${kpis.total_stores}`} icon={StoreIcon} accent="bg-gray-100 text-gray-700" sub={`${kpis.total_stores-kpis.active_stores} yet to submit today's sales`} />
            {!readOnly && monthly && (
              <KpiCard
                testId="kpi-monthly-target"
                label="Monthly Target (per store)"
                value={formatNum(monthly.target_per_store || 0)}
                icon={Target}
                accent="bg-red-50 text-brand-red"
                sub={`${monthly.working_days_in_period} working days × ${formatNum(monthly.target_per_store_daily || 400)} cups (excl. ${monthly.sundays_in_period} Sundays)`}
              />
            )}
            {!readOnly && (<>
              <KpiCard testId="kpi-cash" label="Cash Collected" value={formatLakhs(kpis.cash || 0)} icon={Wallet} accent="bg-gray-100 text-gray-700" />
              <KpiCard testId="kpi-upi" label="UPI Collected" value={formatLakhs(kpis.upi || 0)} icon={Smartphone} accent="bg-red-50 text-brand-red" />
            </>)}
            {readOnly && (<>
              <KpiCard testId="kpi-damaged" label="Shrinkage" value={formatNum(kpis.damaged)} icon={AlertTriangle} accent="bg-amber-50 text-amber-700" />
              <KpiCard testId="kpi-attend" label="Attendance" value={formatNum(attendance.present+attendance.late)} icon={Users} accent="bg-green-50 text-green-700" sub={`of ${kpis.total_stores}`} />
            </>)}
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card className="lg:col-span-2 p-5 rounded-xl border-gray-200 shadow-sm bg-white">
              <div className="flex items-center justify-between">
                <div><div className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">7-day Sales Trend</div>
                  <div className="font-display text-xl font-bold text-gray-900 mt-0.5">Cups vs target</div></div>
                <TrendingUp className="h-4 w-4 text-brand-red" />
              </div>
              <div className="mt-4 h-64" data-testid="trend-chart">
                <ResponsiveContainer>
                  <LineChart data={trend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => d.slice(5)} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 12 }} />
                    <Line type="monotone" dataKey="glasses" stroke={RED} strokeWidth={2.5} dot={{ r: 3 }} name="Cups" />
                    <Line type="monotone" dataKey="target" stroke={GRAY} strokeDasharray="4 4" strokeWidth={1.5} dot={false} name="Target" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Card>

            <Card className="p-5 rounded-xl border-gray-200 shadow-sm bg-white">
              <div className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Attendance</div>
              <div className="font-display text-xl font-bold text-gray-900 mt-0.5">Today</div>
              <div className="mt-4 h-56" data-testid="attendance-chart">
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={attData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={80} paddingAngle={2}>
                      {attData.map((e) => <Cell key={e.name} fill={e.fill} />)}
                    </Pie>
                    <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card className="p-5 rounded-xl border-gray-200 shadow-sm bg-white">
              <div className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Top 5 Stores Today</div>
              <div className="font-display text-xl font-bold text-gray-900 mt-0.5">By cups sold</div>
              <div className="mt-4 h-64" data-testid="top-stores-chart">
                <ResponsiveContainer>
                  <BarChart data={top_stores} layout="vertical" margin={{ left: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="store_name" tick={{ fontSize: 10 }} width={120} />
                    <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 12 }} />
                    <Bar dataKey="glasses" name="Cups" fill={RED} radius={[0, 6, 6, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>

            <Card className="p-5 rounded-xl border-gray-200 shadow-sm bg-white">
              <div className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">City-wise Sales</div>
              <div className="font-display text-xl font-bold text-gray-900 mt-0.5">Today</div>
              <div className="mt-4 h-64" data-testid="city-chart">
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={city_wise} dataKey="sales" nameKey="city" cx="50%" cy="50%" outerRadius={80} label={(e) => e.city}>
                      {city_wise.map((c, i) => <Cell key={c.city} fill={[RED, DARK, "#4B5563", "#9CA3AF", "#F59E0B", "#16A34A", "#3B82F6"][i % 7]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => formatINR(v)} contentStyle={{ borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </Card>

            <Card className="p-5 rounded-xl border-gray-200 shadow-sm bg-white">
              <div className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Stock Health</div>
              <div className="font-display text-xl font-bold text-gray-900 mt-0.5">Days of cover</div>
              <div className="mt-4 space-y-3" data-testid="stock-health">
                {stock_health?.map((s) => {
                  const noData = s.no_data || 0;
                  const total = s.low + s.ok + s.healthy + noData;
                  const denom = total || 1;
                  return (
                  <div key={s.item}>
                    <div className="flex items-center justify-between text-sm mb-1.5">
                      <div className="flex items-center gap-2"><Package className="h-4 w-4 text-gray-600" /><span className="font-semibold">{s.item}</span></div>
                      <span className="text-xs text-gray-500">{s.low+s.ok+s.healthy+(s.no_data||0)} stores</span>
                    </div>
                    <div className="flex h-3 rounded-full overflow-hidden bg-gray-100">
                      <div className="bg-red-500" style={{ width: `${(s.low/denom)*100}%` }} title={`${s.low} low`} />
                      <div className="bg-amber-500" style={{ width: `${(s.ok/denom)*100}%` }} title={`${s.ok} ok`} />
                      <div className="bg-green-500" style={{ width: `${(s.healthy/denom)*100}%` }} title={`${s.healthy} healthy`} />
                      <div className="bg-gray-400" style={{ width: `${(noData/denom)*100}%` }} title={`${noData} no data`} />
                    </div>
                    <div className="mt-1 flex text-[11px] gap-3 text-gray-600">
                      <span><span className="inline-block h-2 w-2 rounded-full bg-red-500 mr-1" />{s.low} low (&lt;3d)</span>
                      <span><span className="inline-block h-2 w-2 rounded-full bg-amber-500 mr-1" />{s.ok} ok (3-7d)</span>
                      <span><span className="inline-block h-2 w-2 rounded-full bg-green-500 mr-1" />{s.healthy} healthy</span>
                      <span><span className="inline-block h-2 w-2 rounded-full bg-gray-400 mr-1" />{noData} no data</span>
                    </div>
                  </div>
                  );
                })}
                {/* ITEM 7 re-audit — Cups row sourced from Promoter's latest closing_stock per store */}
                <div data-testid="days-of-cover-cups">
                  <div className="flex items-center justify-between text-sm mb-1.5">
                    <div className="flex items-center gap-2"><Package className="h-4 w-4 text-gray-600" /><span className="font-semibold">Cups</span></div>
                    <span className="text-xs text-gray-500">{cupsCoverBuckets.low + cupsCoverBuckets.ok + cupsCoverBuckets.healthy} reporting</span>
                  </div>
                  <div className="flex h-3 rounded-full overflow-hidden bg-gray-100">
                    <div className="bg-red-500" style={{ width: `${(cupsCoverBuckets.low/(cupsCoverBuckets.low+cupsCoverBuckets.ok+cupsCoverBuckets.healthy||1))*100}%` }} />
                    <div className="bg-amber-500" style={{ width: `${(cupsCoverBuckets.ok/(cupsCoverBuckets.low+cupsCoverBuckets.ok+cupsCoverBuckets.healthy||1))*100}%` }} />
                    <div className="bg-green-500" style={{ width: `${(cupsCoverBuckets.healthy/(cupsCoverBuckets.low+cupsCoverBuckets.ok+cupsCoverBuckets.healthy||1))*100}%` }} />
                  </div>
                  <div className="mt-1 flex text-[11px] gap-3 text-gray-600">
                    <span><span className="inline-block h-2 w-2 rounded-full bg-red-500 mr-1" />{cupsCoverBuckets.low} low (&lt;100 left)</span>
                    <span><span className="inline-block h-2 w-2 rounded-full bg-amber-500 mr-1" />{cupsCoverBuckets.ok} ok (100-499)</span>
                    <span><span className="inline-block h-2 w-2 rounded-full bg-green-500 mr-1" />{cupsCoverBuckets.healthy} healthy (500+)</span>
                    {cupsCoverBuckets.noData > 0 && (
                      <span className="text-gray-400"><span className="inline-block h-2 w-2 rounded-full bg-gray-300 mr-1" />{cupsCoverBuckets.noData} no data</span>
                    )}
                  </div>
                </div>
              </div>
              {!readOnly && paymentData.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Cash vs UPI vs Card (last 7 days)</div>
                  <div className="h-32 mt-2" data-testid="payment-chart">
                    <ResponsiveContainer>
                      <PieChart>
                        <Pie data={paymentData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={30} outerRadius={55} paddingAngle={2}>
                          {paymentData.map((e) => <Cell key={e.name} fill={e.fill} />)}
                        </Pie>
                        <Legend iconType="circle" wrapperStyle={{ fontSize: 10 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </Card>
          </div>

          {/* Daily Digest */}
          {digest && (
            <Card className="p-5 rounded-xl border-gray-200 shadow-sm bg-gradient-to-br from-neutral-900 to-neutral-800 text-white" data-testid="digest-card">
              <div className="flex items-center gap-2 mb-2">
                <Zap className="h-4 w-4 text-brand-red" />
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-neutral-400">End-of-day digest</div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div><div className="text-[11px] uppercase tracking-widest text-neutral-500">Cups</div><div className="font-display text-2xl font-black">{formatNum(digest.total_glasses)}</div></div>
                <div><div className="text-[11px] uppercase tracking-widest text-neutral-500">Sales</div><div className="font-display text-2xl font-black">{formatLakhs(digest.total_sales)}</div></div>
                <div><div className="text-[11px] uppercase tracking-widest text-neutral-500">Top store</div><div className="font-semibold text-sm mt-1">{digest.top_store?.name || "—"}</div><div className="text-[11px] text-neutral-400">{formatNum(digest.top_store?.glasses)} cups</div></div>
                <div><div className="text-[11px] uppercase tracking-widest text-neutral-500">Open issues</div><div className="font-display text-2xl font-black">{digest.open_issues}</div></div>
              </div>
            </Card>
          )}

          {/* Store table */}
          <Card className="rounded-xl border-gray-200 shadow-sm bg-white overflow-hidden">
            <div className="p-5 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div><div className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Store status</div>
                <div className="font-display text-xl font-bold text-gray-900 mt-0.5">All stores — live</div></div>
              <Input data-testid="store-filter-input" placeholder="Filter store, code, city…"
                value={filter} onChange={(e) => setFilter(e.target.value)} className="max-w-xs" />
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="stores-table">
                <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                  <tr>
                    <th className="text-left px-4 py-3">Live</th>
                    <th className="text-left px-4 py-3">Store</th>
                    <th className="text-left px-4 py-3">City</th>
                    <th className="text-left px-4 py-3">Promoter</th>
                    <th className="text-right px-4 py-3">Cups Sold</th>
                    <th className="text-right px-4 py-3">Sales</th>
                    <th className="text-right px-4 py-3">Target %</th>
                    <th className="text-right px-4 py-3">Cups Remaining</th>
                    <th className="text-right px-4 py-3">BIB/CO2 left</th>
                    <th className="text-left px-4 py-3">Attendance</th>
                    <th className="text-left px-4 py-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => (
                    <tr
                      key={r.store_id}
                      onClick={() => nav(`/stores/${r.store_id}`)}
                      className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer"
                      data-testid={`store-row-${r.store_no}`}
                    >
                      <td className="px-4 py-3">
                        {/* ITEM B.9 (2026-02-29) — Live indicator: blinking GREEN when attendance
                            marked today, RED when there is any open issue, else neutral dot. */}
                        {r.has_open_issue ? (
                          <span data-testid={`live-red-${r.store_no}`}
                                className="inline-block h-2.5 w-2.5 rounded-full bg-red-500 animate-pulse ring-2 ring-red-200" />
                        ) : r.attendance_today ? (
                          <span data-testid={`live-green-${r.store_no}`}
                                className="inline-block h-2.5 w-2.5 rounded-full bg-green-500 animate-pulse ring-2 ring-green-200" />
                        ) : (
                          <span data-testid={`live-none-${r.store_no}`}
                                className="inline-block h-2.5 w-2.5 rounded-full bg-gray-300" />
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-gray-900">{r.store_name}</div>
                        <div className="text-xs text-gray-500">{r.store_code}</div>
                      </td>
                      <td className="px-4 py-3 text-gray-700">{r.city}</td>
                      <td className="px-4 py-3 text-gray-700">{r.promoter}</td>
                      <td className="px-4 py-3 text-right font-semibold">{formatNum(r.glasses_sold)}</td>
                      <td className="px-4 py-3 text-right font-semibold">{formatINR(r.sales)}</td>
                      <td className="px-4 py-3 text-right">
                        <span className={`font-semibold ${r.target_pct>=80?"text-green-700":r.target_pct>=50?"text-amber-700":"text-red-700"}`}>{r.target_pct}%</span>
                      </td>
                      {/* ITEM B.7 / B.10 — Cups Remaining (single-source-of-truth) */}
                      <td className={`px-4 py-3 text-right font-mono ${r.cups_remaining != null && r.cups_remaining < 100 ? "text-red-700 font-bold" : "text-gray-800"}`}
                          data-testid={`cups-remaining-${r.store_no}`}>
                        {r.cups_remaining != null ? formatNum(r.cups_remaining) : "—"}
                      </td>
                      <td className={`px-4 py-3 text-right text-xs font-semibold ${r.low_stock ? "text-red-700" : r.glasses_left != null && r.glasses_left < 500 ? "text-amber-700" : "text-gray-700"}`}
                          data-testid={`cups-left-${r.store_no}`}>
                        {r.glasses_left != null ? (
                          <>
                            <div className="font-mono text-sm">{formatNum(r.glasses_left)}</div>
                            <div className="text-[10px] text-gray-500 font-normal">
                              BIB {r.bib_glasses_left != null ? formatNum(r.bib_glasses_left) : "—"} · CO2 {r.co2_glasses_left != null ? formatNum(r.co2_glasses_left) : "—"}
                            </div>
                          </>
                        ) : "—"}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="outline" className={
                          r.attendance === "Present" ? "border-green-300 text-green-700 bg-green-50" :
                          r.attendance === "Late" ? "border-amber-300 text-amber-700 bg-amber-50" :
                          "border-gray-300 text-gray-500 bg-gray-50"
                        }>{r.attendance}</Badge>
                      </td>
                      <td className="px-4 py-3">
                        {!r.entered ? <Badge variant="outline" className="border-gray-300 text-gray-500">No entry</Badge>
                          : r.payment_flag ? <Badge variant="destructive">Payment mismatch</Badge>
                          : r.approved ? <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Approved</Badge>
                          : <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">Pending review</Badge>}
                      </td>
                    </tr>
                  ))}
                  {filtered.length === 0 && (
                    <tr><td colSpan={11} className="px-4 py-8 text-center text-gray-500">No stores match filter</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </main>
      </div>
    </div>
  );
}
